// Sanctioned inline-suggestions pattern: a light, dashed section with chip actions (the original
// QuickAdd preset look), reused across the app. Presentation-only - callers compute/filter the
// items and pass already-translated text; overlay hosts own their own callout and positioning.
import { IPActionButton, IPDefaultButton, IPIconButton, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { useTheme } from '@fluentui/react/lib/Theme';
import { useState } from 'react';

export interface IInlineSuggestionItem {
  id: string | number;
  label: string;
  tooltip?: string;
  iconName?: string;
}

export interface IInlineSuggestionsText {
  show?: string;
  label: string;
  hint: string;
  dismiss: string;
}

export type InlineSuggestionsVariant = 'default' | 'ai';

export interface IInlineSuggestionsProps<T extends IInlineSuggestionItem> {
  items: T[];
  onSelect: (item: T) => void;
  text: IInlineSuggestionsText;
  // 'ai' tints the section with the theme brand colors to distinguish generated suggestions.
  variant?: InlineSuggestionsVariant;
  expanded?: boolean;
  defaultExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  // Shows the chips without letting them be picked, so a host's layout stays the same while it is disabled.
  disabled?: boolean;
}

const InlineSuggestions = <T extends IInlineSuggestionItem>(props: IInlineSuggestionsProps<T>) => {
  const theme = useTheme();
  //the accent tint is defined per theme, so the ai section has the right tint in light and dark
  const chrome =
    props.variant === 'ai'
      ? {
          background: theme.semanticColors.accentTintBackground,
          border: theme.palette.themeTertiary,
        }
      : { background: theme.palette.neutralLighterAlt, border: theme.palette.neutralTertiaryAlt };
  const [internalExpanded, setInternalExpanded] = useState<boolean>(props.defaultExpanded ?? false);
  const expanded = props.expanded ?? internalExpanded;

  const setExpanded = (next: boolean) => {
    if (props.expanded === undefined) setInternalExpanded(next);
    props.onExpandedChange?.(next);
  };

  if (props.items.length === 0) return null;

  if (!expanded) {
    // Always-expanded hosts leave `show` undefined: there is no collapsed lightbulb to offer.
    if (props.text.show === undefined) return null;

    return (
      <IPActionButton
        iconProps={{ iconName: 'Lightbulb' }}
        text={props.text.show}
        onClick={() => setExpanded(true)}
      />
    );
  }

  return (
    <IPStack
      tokens={{ childrenGap: 8 }}
      styles={{
        root: {
          padding: '10px 12px',
          borderRadius: theme.effects.roundedCorner4,
          backgroundColor: chrome.background,
          border: `1px dashed ${chrome.border}`,
        },
      }}
    >
      <IPStack.Item>
        <IPStack horizontal horizontalAlign="space-between" verticalAlign="start" tokens={{ childrenGap: 8 }}>
          <IPStack.Item grow styles={{ root: { minWidth: 0 } }}>
            <IPText styles={{ root: { display: 'block', fontSize: 12, fontWeight: 600 } }}>
              {props.text.label}
            </IPText>
          </IPStack.Item>
          <IPStack.Item styles={{ root: { marginRight: -6, marginTop: -6 } }}>
            <IPIconButton
              iconProps={{ iconName: 'Cancel' }}
              title={props.text.dismiss}
              disabled={props.disabled}
              onClick={() => setExpanded(false)}
            />
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
      <IPStack.Item>
        <IPText styles={{ root: { display: 'block', fontSize: 12 } }}>{props.text.hint}</IPText>
      </IPStack.Item>
      <IPStack.Item>
        <IPStack horizontal wrap tokens={{ childrenGap: 8 }}>
          {props.items.map((item) => (
            <IPDefaultButton
              key={item.id}
              iconProps={{ iconName: item.iconName ?? 'Add' }}
              text={item.label}
              title={item.tooltip ?? item.label}
              disabled={props.disabled}
              onClick={() => {
                if (!props.disabled) props.onSelect(item);
              }}
              styles={{
                root: {
                  borderRadius: theme.effects.roundedCorner4,
                  height: 28,
                  minWidth: 0,
                  padding: '0 10px',
                },
                label: { fontSize: 13, fontWeight: 400 },
              }}
            />
          ))}
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );
};

export default InlineSuggestions;
